import { isEmpty } from 'lodash';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { ChatEnum, EventType } from '../../../../Chat.enum.ts';
import { updateEventAttachmentParser } from '../../../../Chat.functions.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatService } from '../../../../Chat.service.ts';
import {
  tAttachment,
  tChatEvent,
  tUpdateEventData,
} from '../../../../Chat.types.ts';
import useMessageBubble from '../../../MessageContainer/components/EventBubble/components/MessageBubble/MessageBubble.hook.tsx';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { tLeadsalesAIActionHook } from './NotesAction.types.ts';

export default function useLeadsalesAIAction(): tLeadsalesAIActionHook {
  const [loading, setLoading] = useState<boolean>(false);

  const {
    selectedChat,
    setSelectedChat,
    chatEvents,
    setChatEvents,
    leadsalesAI,
    setLeadsalesAI,
  } = useChatContext();
  const { addAlert } = useToastContext();
  const { currentUserId } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { replaceMessageWithVariable } = useMessageBubble();
  const {
    messageBarValue,
    setMessageBarValue,
    setSelectedAction,
    setChatAttachments,
  } = useMessageBarContext();

  async function createNewLeadsalesAI(
    title: string,
    attachments?: tAttachment[],
    customText?: string,
  ) {
    try {
      if (!selectedChat?.id || !currentUserId || !chatEvents) {
        return;
      }

      setLoading(true);

      const leadsalesAIId = uuid();

      // Use customText if provided, otherwise use messageBarValue
      const aiText = customText !== undefined ? customText : messageBarValue;

      await ChatService.createLeadsalesAI({
        chatId: selectedChat.id,
        data: {
          id: leadsalesAIId,
          content: {
            text: replaceMessageWithVariable(aiText) || '',
            attachments:
              attachments?.map((attachment) =>
                updateEventAttachmentParser(attachment),
              ) || [],
          },
          created_by: currentUserId,
          title: title,
          leadsales_ai: {
            title: title,
            type: 0,
          },
        },
      });

      // Create new LEADSALES_AI event
      const newLeadsalesAI: tChatEvent = {
        agendaEvent: null,
        chatID: selectedChat.id,
        content: {
          attachments: attachments || [],
          linkPreview: null,
          quotedEventID: null,
          text: replaceMessageWithVariable(aiText) || '',
        },
        createdAt: new Date().toISOString(),
        createdBy: currentUserId,
        deletedAt: '',
        followUp: null,
        id: leadsalesAIId,
        message: null,
        note: {
          title: title,
        },
        type: EventType.LEADSALES_AI,
        updatedAt: new Date().toISOString(),
      };

      // Add to leadsalesAI list instead of notes
      setLeadsalesAI([newLeadsalesAI, ...leadsalesAI]);
      setChatEvents([...chatEvents, newLeadsalesAI]);

      setMessageBarValue('');
      setSelectedAction(ChatEnum.CHAT);
      setChatAttachments([]);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos crear el análisis de Leadsales AI: ${error}`,
      });
    }
    setLoading(false);
  }

  async function deleteEventLeadsalesAI(eventId: string) {
    try {
      if (
        !currentWorkspaceId ||
        !currentUserId ||
        isEmpty(leadsalesAI) ||
        !chatEvents
      ) {
        return;
      }

      setLoading(true);

      await ChatService.deleteEvent(
        {
          eventId: eventId,
          workspaceId: currentWorkspaceId,
        },
        currentUserId,
      );

      const newEvents = selectedChat?.chatEvents?.events?.filter(
        (event) => event.id !== eventId,
      );

      const newLeadsalesAI = leadsalesAI.filter((ai) => ai.id !== eventId);

      if (!selectedChat || !newEvents) {
        return;
      }

      setLeadsalesAI(newLeadsalesAI);
      setSelectedChat({
        ...selectedChat,
        chatEvents: {
          ...selectedChat.chatEvents,
          events: newEvents,
        },
      });
      setChatEvents(chatEvents?.filter((event) => event.id !== eventId));
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Análisis de Leadsales AI eliminado correctamente',
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos eliminar el análisis de Leadsales AI: ${error}`,
      });
    }
    setLoading(false);
  }

  async function updateEvent(eventId: string, data: tUpdateEventData) {
    try {
      if (!currentWorkspaceId || !currentUserId || !chatEvents) {
        return;
      }

      await ChatService.updateEvent(
        {
          eventId: eventId,
          workspaceId: currentWorkspaceId,
          data: {
            ...data,
            content: {
              ...data.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
            },
          },
        },
        currentUserId,
      );

      if (!selectedChat || isEmpty(leadsalesAI)) {
        return;
      }

      const updatedLeadsalesAI: tChatEvent[] = leadsalesAI.map((ai) => {
        if (ai.id === eventId) {
          return {
            ...ai,
            content: {
              ...ai.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
            },
            note: {
              title: data?.title || ai.note?.title || '',
            },
            updatedAt: new Date().toISOString(),
          };
        }
        return ai;
      });

      setLeadsalesAI(updatedLeadsalesAI);

      const updatedChatEvents: tChatEvent[] = chatEvents.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            content: {
              ...event.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
            },
            note: {
              title: data?.title || event.note?.title || '',
            },
            updatedAt: new Date().toISOString(),
          };
        }
        return event;
      });

      setChatEvents(updatedChatEvents);

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Análisis de Leadsales AI actualizado correctamente',
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos actualizar el análisis de Leadsales AI: ${error}`,
      });
    }
  }

  return {
    loading,
    createNewLeadsalesAI,
    deleteEventLeadsalesAI,
    updateEvent,
  };
}
